import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  message,
  Select,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import CompanyServices from "../../services/CompanyServices";
import { translate } from "../../locale";
import DashServices from "../../services/DashServices";
import PillServices from "../../services/PillServices";

const { TextArea } = Input;

const SimpleReactValidatorMessage = (message) => (
  <div className="text-red-600 pt-2 text-xs">{message}</div>
);

const AddPill = ({ visible, onComplete, onCancel, pillToEdit }) => {
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [values, setValues] = useState({
    capsule_strapi_id: "",
    enterprises: [""],
    type: "standard",
    name: "",
    description: "",
    url: "",
  });
  const [enterprises, setEnterprises] = useState(null);
  const types = [
    { value: "standard", label: translate("Estándar") },
    { value: "education", label: translate("Educación") },
  ];
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const [inEditMode, setInEditMode] = useState(false);
  const [initialEnterprise, setInitialEnterprise] = useState(0);

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: SimpleReactValidatorMessage,
      messages: {
        required: translate("Campo requerido"),
        url: translate("URL inválida"),
      },
    }),
  );

  useEffect(() => {
    if (pillToEdit && Object.keys(pillToEdit).length > 0) {
      setInEditMode(true);
      const enterprisesInfoValues = pillToEdit.enterprises_info.map(
        (enterprise) => ({ value: enterprise.id, label: enterprise.name }),
      );
      setInitialEnterprise(enterprisesInfoValues[0].value);
      setValues({
        capsule_strapi_id: pillToEdit.capsule_strapi_id,
        enterprises: enterprisesInfoValues || "",
        type: pillToEdit.type,
        name: pillToEdit.name,
        description: pillToEdit.description,
        url: pillToEdit.url,
      });
    }
  }, [pillToEdit]);

  useEffect(() => {
    if (visible !== null) {
      setActive(visible);
      if (visible === true) {
        getEnterprises();
      }
    }
  }, [visible]);

  const getEnterprises = async () => {
    try {
      setLoading(true);
      const response = await CompanyServices.getCompanies({
        page_size: 1000,
        page_num: 1,
      });
      if (response.status >= 200 && response.status < 300) {
        setEnterprises(
          response.data.enterprises.map((i) => ({
            value: i.id,
            label: i.name,
          })),
        );
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const disabled = () => {
    return !simpleValidator.current.allValid();
  };

  const enterpriseValue = () => {
    let eValue = values.enterprises;
    if (Array.isArray(values.enterprises)) {
      // CASO 2: NO MODIFICO LA EMPRESA
      // LLEGA COMO OBJETO [{value: int, label: str}]
      if (
        values.enterprises[0] &&
        typeof values.enterprises[0] === "object" &&
        !Array.isArray(values.enterprises[0]) &&
        "value" in values.enterprises[0]
      ) {
        eValue = [values.enterprises[0].value];
      }
    }
    return eValue;
  };

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("Todos los datos son requeridos");
      return;
    }
    if (inEditMode) {
      updatePill();
    } else {
      savePill();
    }
  };
  const updatePill = async () => {
    try {
      // CASO 1: MODIFICO LA EMPRESA
      // LLEGA COMO ARRAY [int]
      const eValue = enterpriseValue();

      const dataPill = {
        pill_id: pillToEdit.id,
        name: values.name,
        description: values.description,
        type: values.type,
        url: values.type === "education" ? values.url : "",
        enterprises: eValue[0] == initialEnterprise ? [] : eValue,
        enterprises_delete:
          eValue[0] == initialEnterprise || !initialEnterprise
            ? []
            : [initialEnterprise],
        capsule_strapi_id: values.capsule_strapi_id,
      };
      const res = await PillServices.edit(dataPill);
      if (res) {
        if (res.status >= 200 && res.status < 300) {
          message.success(
            translate("La {0} se guardó exitosamente.", translate("píldora")),
          );
        } else {
          message.success(res.data.message, 6);
        }
        resetForm();
        if (onComplete) {
          onComplete(res.data.pill);
        }
      } else {
        setError(
          translate("La {0} no pudo ser guardada.", translate("píldora")),
        );
      }
    } catch (e) {
      console.error("savePill error: ", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const savePill = async () => {
    try {
      const res = await DashServices.savePill(values);
      if (res) {
        if (res.status >= 200 && res.status < 300) {
          message.success(
            translate("La {0} se guardó exitosamente.", translate("píldora")),
          );
        } else {
          message.success(res.data.message, 6);
        }
        resetForm();
        if (onComplete) {
          onComplete(res.data.pill);
        }
      } else {
        setError(
          translate("La {0} no pudo ser guardada.", translate("píldora")),
        );
      }
    } catch (e) {
      console.error("savePill error: ", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setValues({
      capsule_strapi_id: "",
      enterprises: [""],
      type: "standard",
      name: "",
      description: "",
      url: "",
    });
    setError("");
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    simpleValidator.current.purgeFields();
    forceUpdate();
  };

  const handleChange = (name, value) => {
    simpleValidator.current.purgeFields();
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleCancel = () => {
    resetForm();
    setActive(false);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      open={active}
      title={translate("Agregar píldora")}
      onOk={null}
      onCancel={handleCancel}
      footer={false}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-6"
          closable
          onClose={() => setError("")}
        />
      )}
      <Form
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete="off"
        className="w-full"
      >
        <Form.Item>
          <Select
            name="enterprises"
            onChange={(value) => {
              setValues((v) => ({ ...v, enterprises: [value] }));
            }}
            value={values.enterprises[0]}
          >
            <Select.Option value="">
              {translate("Seleccione empresa")}
            </Select.Option>
            {enterprises !== null &&
              enterprises.map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
          </Select>
          {simpleValidator.current.message(
            "enterprises",
            values.enterprises,
            "required",
          )}
        </Form.Item>
        <Form.Item>
          <Select
            name="type"
            onChange={(value) => {
              setValues((v) => ({ ...v, type: value }));
            }}
            value={values.type}
          >
            <Select.Option value="">
              {translate("Seleccione tipo")}
            </Select.Option>
            {types.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
          {simpleValidator.current.message("type", values.type, "required")}
        </Form.Item>
        <Form.Item>
          <Input
            placeholder="Nombre"
            className="border-custom_primary"
            name="name"
            onChange={(e) => handleChange("name", e.target.value)}
            onBlur={handleBlur}
            value={values.name}
            maxLength={50}
          />
          {simpleValidator.current.message("name", values.name, "required")}
        </Form.Item>
        <Form.Item>
          <TextArea
            rows={4}
            placeholder={translate("Descripción")}
            className="border-custom_primary"
            name="description"
            onChange={(e) => handleChange("description", e.target.value)}
            onBlur={handleBlur}
            value={values.description}
          />
          {simpleValidator.current.message(
            "description",
            values.name,
            "required",
          )}
        </Form.Item>
        {values.type === "education" && (
          <Form.Item>
            <Input
              placeholder="Url"
              className="border-custom_primary"
              name="url"
              onChange={(e) => handleChange("url", e.target.value)}
              onBlur={handleBlur}
              value={values.url}
            />
            {(!values.capsule_strapi_id || values.url) &&
              simpleValidator.current.message(
                "url",
                values.url,
                "required|url",
              )}
          </Form.Item>
        )}
        <Form.Item>
          <Input
            placeholder="Strapi ID (Op)"
            className="border-custom_primary"
            name="capsule_strapi_id"
            onChange={(e) => handleChange("capsule_strapi_id", e.target.value)}
            onBlur={handleBlur}
            value={values.capsule_strapi_id}
          />
          {simpleValidator.current.message(
            "strapi_id",
            values.strapi_id,
            "integer",
          )}
        </Form.Item>
        <Form.Item>
          <Row align="end" gutter={16}>
            <Col>
              <Button key="back" onClick={handleCancel}>
                {translate("Cancelar")}
              </Button>
            </Col>
            {inEditMode ? (
              <Col>
                <Button
                  disabled={disabled()}
                  htmlType="submit"
                  type="primary"
                  className="bg-custom_primary"
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  ) : (
                    "Editar"
                  )}
                </Button>
              </Col>
            ) : (
              <Col>
                <Button
                  disabled={disabled()}
                  htmlType="submit"
                  type="primary"
                  className="bg-custom_primary"
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  ) : (
                    "Agregar"
                  )}
                </Button>
              </Col>
            )}
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

AddPill.propTypes = {
  visible: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  pillToEdit: PropTypes.object,
};

export default AddPill;
